import { EventType, InterruptionType } from './game-event';

export type Sport =
  | 'ICE_HOCKEY'
  | 'FLOORBALL'
  | 'INDOOR_HOCKEY'
  | 'FIELD_HOCKEY';

// Every league had this sport before leagues carried one.
export const DEFAULT_SPORT: Sport = 'ICE_HOCKEY';

type SportGate = (sport: Sport) => boolean;

export const isHockeyVariant: SportGate = (sport) =>
  sport === 'INDOOR_HOCKEY' || sport === 'FIELD_HOCKEY';

export const isFloorball: SportGate = (sport) => sport === 'FLOORBALL';

const isIceHockey: SportGate = (sport) => sport === 'ICE_HOCKEY';

const isNotHockeyVariant: SportGate = (sport) => !isHockeyVariant(sport);

const isNotIceHockey: SportGate = (sport) => !isIceHockey(sport);

// Indoor and field hockey play four quarters, ice hockey and floorball three periods.
export function regularPeriodCount(sport: Sport): number {
  return isHockeyVariant(sport) ? 4 : 3;
}

// Event types only some sports collect. Every type not listed here is
// collected for all sports.
const SPORT_SPECIFIC_EVENT_TYPES: Partial<Record<EventType, SportGate>> = {
  face_off: isNotHockeyVariant,
  puckPossession: isNotHockeyVariant,
  oddMenRush: isNotHockeyVariant,
  gameRestart: isNotIceHockey,
  turnover: isFloorball
};

// Interruption types only some sports collect. Every type not listed here is
// collected for all sports. Hockey variants only stop the clock for goals,
// timeouts and unspecified reasons (besides the period start and end).
// Floorball has no icing or offside, and play goes on while the goalkeeper
// holds the ball; its fouls follow the IFF Rules of the Game.
const SPORT_SPECIFIC_INTERRUPTION_TYPES: Partial<
  Record<InterruptionType, SportGate>
> = {
  icing: isIceHockey,
  offside: isIceHockey,
  puck_blocked_by_goalkeeper: isIceHockey,
  puck_out: isNotHockeyVariant,
  penalty: isNotHockeyVariant,
  timeout: isNotIceHockey,
  incorrect_pass_back: isFloorball,
  high_stick: isFloorball,
  kicking_violation: isFloorball,
  stick_hit: isFloorball,
  body_contact: isFloorball,
  obstruction: isFloorball,
  foul: isFloorball,
  official_touches_ball: isFloorball,
  unnatural_interruption: isFloorball
};

export function collectsEventType(sport: Sport, eventType: EventType): boolean {
  return isCollectedBy(SPORT_SPECIFIC_EVENT_TYPES[eventType], sport);
}

export function collectsInterruptionType(
  sport: Sport,
  interruptionType: InterruptionType
): boolean {
  return isCollectedBy(
    SPORT_SPECIFIC_INTERRUPTION_TYPES[interruptionType],
    sport
  );
}

function isCollectedBy(gate: SportGate | undefined, sport: Sport): boolean {
  return gate ? gate(sport ?? DEFAULT_SPORT) : true;
}
